"use client";

import React from "react";
import Link from "next/link";
import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import DataTable from "@/components/ui/DataTable";
import Pagination from "@/components/tables/Pagination";
import Button from "@/components/ui/button/Button";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import SearchableSelect from "@/components/ui/SearchableSelect";
import DropdownSelect from "@/components/ui/DropdownSelect";
import { ChevronDownIcon } from "@/icons";
import { rmcClient } from "@/lib/rmcClient";
import { useQuery } from "@tanstack/react-query";
import { twMerge } from "tailwind-merge";

const PAGE_SIZE = 20;

const EMI_SYNC_OPTIONS = [
  { value: "", label: "Any" },
  { value: "true", label: "Auto sync on" },
  { value: "false", label: "Auto sync off" },
];

const defaultFilterState = () => ({
  search: "",
  vehicle_id: "",
  unlinked_vehicle: false,
  department_code: "",
  internal_vehicle_code: "",
  loan_start_after: "",
  loan_end_before: "",
  loan_end_after: "",
  first_emi_after: "",
  first_emi_before: "",
  emi_auto_sync: "",
  expiring_within_days: "",
  bank_name: "",
  asset_type: "",
});

function buildListQueryParams(filters, paginationOff, page) {
  const p = {};
  const t = (s) => (typeof s === "string" ? s.trim() : "");

  if (t(filters.search)) p.search = t(filters.search);
  if (t(filters.bank_name)) p.bank_name = t(filters.bank_name);
  if (t(filters.asset_type)) p.asset_type = t(filters.asset_type);

  if (filters.vehicle_id) {
    const vid = Number(filters.vehicle_id);
    if (Number.isFinite(vid) && vid > 0) p.vehicle_id = vid;
  }
  if (filters.unlinked_vehicle) p.unlinked_vehicle = true;

  if (t(filters.department_code)) {
    const n = Number(t(filters.department_code));
    if (Number.isFinite(n)) p.department_code = n;
  }
  if (t(filters.internal_vehicle_code)) {
    const n = Number(t(filters.internal_vehicle_code));
    if (Number.isFinite(n)) p.internal_vehicle_code = n;
  }

  if (t(filters.loan_start_after)) p.loan_start_after = t(filters.loan_start_after);
  if (t(filters.loan_end_before)) p.loan_end_before = t(filters.loan_end_before);
  if (t(filters.loan_end_after)) p.loan_end_after = t(filters.loan_end_after);
  if (t(filters.first_emi_after)) p.first_emi_after = t(filters.first_emi_after);
  if (t(filters.first_emi_before)) p.first_emi_before = t(filters.first_emi_before);

  if (filters.emi_auto_sync === "true" || filters.emi_auto_sync === "false") {
    p.emi_auto_sync_enabled = filters.emi_auto_sync === "true";
  }

  if (t(filters.expiring_within_days)) {
    const d = Number(t(filters.expiring_within_days));
    if (Number.isFinite(d) && d > 0) p.expiring_within_days = d;
  }

  if (paginationOff) {
    p.pagination = false;
  } else {
    p.page = page;
    p.page_size = PAGE_SIZE;
  }
  return p;
}

function countActiveFilters(f) {
  let n = 0;
  if (f.search?.trim()) n++;
  if (f.vehicle_id) n++;
  if (f.unlinked_vehicle) n++;
  if (f.department_code?.trim()) n++;
  if (f.internal_vehicle_code?.trim()) n++;
  if (f.loan_start_after?.trim()) n++;
  if (f.loan_end_before?.trim()) n++;
  if (f.loan_end_after?.trim()) n++;
  if (f.first_emi_after?.trim()) n++;
  if (f.first_emi_before?.trim()) n++;
  if (f.emi_auto_sync === "true" || f.emi_auto_sync === "false") n++;
  if (f.expiring_within_days?.trim()) n++;
  if (f.bank_name?.trim()) n++;
  if (f.asset_type?.trim()) n++;
  return n;
}

function fmtMoney(v) {
  if (v == null || v === "") return "—";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(v) {
  if (v == null || v === "") return "—";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

const FLT_LBL = "mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400";
const FLT_INP =
  "h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm text-gray-900 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:placeholder:text-gray-500";

export default function RmcVehicleFinance() {
  const [filters, setFilters] = React.useState(defaultFilterState);
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  const [paginationOff, setPaginationOff] = React.useState(false);
  const [page, setPage] = React.useState(1);

  const queryParams = React.useMemo(
    () => buildListQueryParams(filters, paginationOff, page),
    [filters, paginationOff, page],
  );

  const activeFilterCount = React.useMemo(() => countActiveFilters(filters), [filters]);

  const { data: vehicles = [] } = useQuery({
    queryKey: ["vehicles"],
    queryFn: async () => (await rmcClient.listVehicles()).data,
  });

  const vehicleOptions = React.useMemo(
    () =>
      (Array.isArray(vehicles) ? vehicles : []).map((v) => ({
        ...v,
        _label: [v.registration_no, v.name || v.alias].filter(Boolean).join(" · ") || `Vehicle #${v.id}`,
      })),
    [vehicles],
  );

  const { data: listData, isFetching } = useQuery({
    queryKey: ["vehicleFinanceRecords", queryParams],
    queryFn: async () => (await rmcClient.listVehicleFinanceRecords(queryParams)).data,
    keepPreviousData: true,
  });

  const rows = React.useMemo(() => {
    if (Array.isArray(listData)) return listData;
    if (Array.isArray(listData?.results)) return listData.results;
    if (Array.isArray(listData?.data?.results)) return listData.data.results;
    return [];
  }, [listData]);

  const totalCount = React.useMemo(() => {
    const v = Number(listData?.count);
    if (Number.isFinite(v) && v >= 0) return v;
    const n = Number(listData?.data?.count);
    if (Number.isFinite(n) && n >= 0) return n;
    return rows.length;
  }, [listData, rows.length]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const patchFilter = (partial) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, ...partial }));
  };

  const clearFilters = () => {
    setFilters(defaultFilterState());
    setPaginationOff(false);
    setPage(1);
  };

  const hasAnythingToClear = activeFilterCount > 0 || paginationOff;

  const setUnlinked = (checked) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      unlinked_vehicle: checked,
      ...(checked ? { vehicle_id: "" } : {}),
    }));
  };

  const setVehicleId = (id) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      vehicle_id: id ? String(id) : "",
      ...(id ? { unlinked_vehicle: false } : {}),
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageBreadCrumb pageTitle="Vehicle EMI finance" />
        <Link href="/transport/vehicle-finance/new">
          <Button size="sm" type="button">
            New record
          </Button>
        </Link>
      </div>

      <div className="rounded-2xl border border-gray-200/80 bg-white/90 p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900/50">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200/90 pb-3 dark:border-gray-800">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Filters</h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Search and refine finance records; results update as you change fields.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-brand-500/15 px-2 py-0.5 text-[11px] font-medium text-brand-700 dark:text-brand-300">
                {activeFilterCount} active
              </span>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={clearFilters}
              disabled={!hasAnythingToClear}
            >
              Clear filters
            </Button>
            <button
              type="button"
              onClick={() => setAdvancedOpen((o) => !o)}
              className="inline-flex h-8 items-center gap-1 rounded-lg border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <ChevronDownIcon
                className={twMerge("size-3.5 shrink-0 transition-transform", advancedOpen && "rotate-180")}
              />
              {advancedOpen ? "Hide" : "Show"} advanced
            </button>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-4">
              <Label className={FLT_LBL}>Search</Label>
              <Input
                type="text"
                placeholder="Bank, owner, registration…"
                value={filters.search}
                onChange={(e) => patchFilter({ search: e.target.value })}
                className={FLT_INP}
              />
            </div>
            <div className="lg:col-span-4">
              <SearchableSelect
                label={<span className={FLT_LBL}>Vehicle</span>}
                placeholder="All vehicles…"
                value={filters.vehicle_id ? Number(filters.vehicle_id) : ""}
                onChange={(id) => setVehicleId(id)}
                options={vehicleOptions}
                getValue={(o) => o.id}
                getLabel={(o) => o._label}
                disabled={filters.unlinked_vehicle}
                inputClassName={FLT_INP}
              />
            </div>
            <div className="flex flex-col gap-2 lg:col-span-4">
              <label className={FLT_LBL}>Quick</label>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 dark:border-gray-700 dark:bg-gray-950/60">
                <input
                  type="checkbox"
                  className="rounded border-gray-400 dark:border-gray-600"
                  checked={filters.unlinked_vehicle}
                  onChange={(e) => setUnlinked(e.target.checked)}
                />
                <span className="text-xs text-gray-700 dark:text-gray-300">
                  Only records without a linked vehicle
                </span>
              </label>
            </div>
          </div>

          {advancedOpen && (
            <>
              <div className="grid gap-3 border-t border-gray-200/90 pt-4 dark:border-gray-800 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <Label className={FLT_LBL}>Bank name</Label>
                  <Input
                    type="text"
                    placeholder="e.g. HDFC"
                    value={filters.bank_name}
                    onChange={(e) => patchFilter({ bank_name: e.target.value })}
                    className={FLT_INP}
                  />
                </div>
                <div>
                  <Label className={FLT_LBL}>Asset type</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Truck"
                    value={filters.asset_type}
                    onChange={(e) => patchFilter({ asset_type: e.target.value })}
                    className={FLT_INP}
                  />
                </div>
                <div>
                  <Label className={FLT_LBL}>Expiring within (days)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 30"
                    value={filters.expiring_within_days}
                    onChange={(e) => patchFilter({ expiring_within_days: e.target.value })}
                    className={FLT_INP}
                  />
                </div>
                <div>
                  <DropdownSelect
                    label={<span className={FLT_LBL}>EMI auto sync</span>}
                    placeholder="Any"
                    value={filters.emi_auto_sync}
                    onChange={(v) => patchFilter({ emi_auto_sync: v ?? "" })}
                    options={EMI_SYNC_OPTIONS}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <Label className={FLT_LBL}>Department code</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 47"
                    value={filters.department_code}
                    onChange={(e) => patchFilter({ department_code: e.target.value })}
                    className={FLT_INP}
                  />
                </div>
                <div>
                  <Label className={FLT_LBL}>Internal vehicle code</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 29"
                    value={filters.internal_vehicle_code}
                    onChange={(e) => patchFilter({ internal_vehicle_code: e.target.value })}
                    className={FLT_INP}
                  />
                </div>
              </div>

              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Loan dates
                </p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <Label className={FLT_LBL}>Loan start after</Label>
                    <input
                      type="date"
                      value={filters.loan_start_after}
                      onChange={(e) => patchFilter({ loan_start_after: e.target.value })}
                      className={FLT_INP}
                    />
                  </div>
                  <div>
                    <Label className={FLT_LBL}>Loan end before</Label>
                    <input
                      type="date"
                      value={filters.loan_end_before}
                      onChange={(e) => patchFilter({ loan_end_before: e.target.value })}
                      className={FLT_INP}
                    />
                  </div>
                  <div>
                    <Label className={FLT_LBL}>Loan end after</Label>
                    <input
                      type="date"
                      value={filters.loan_end_after}
                      onChange={(e) => patchFilter({ loan_end_after: e.target.value })}
                      className={FLT_INP}
                    />
                  </div>
                </div>
              </div>

              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  First EMI date
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label className={FLT_LBL}>First EMI after</Label>
                    <input
                      type="date"
                      value={filters.first_emi_after}
                      onChange={(e) => patchFilter({ first_emi_after: e.target.value })}
                      className={FLT_INP}
                    />
                  </div>
                  <div>
                    <Label className={FLT_LBL}>First EMI before</Label>
                    <input
                      type="date"
                      value={filters.first_emi_before}
                      onChange={(e) => patchFilter({ first_emi_before: e.target.value })}
                      className={FLT_INP}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="flex flex-wrap items-center gap-4 border-t border-gray-200/90 pt-3 dark:border-gray-800">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                className="rounded border-gray-400 dark:border-gray-600"
                checked={paginationOff}
                onChange={(e) => {
                  setPaginationOff(e.target.checked);
                  setPage(1);
                }}
              />
              Load full list (no pagination)
            </label>
          </div>
        </div>
      </div>

      <DataTable
        columns={[
          {
            key: "registration_no",
            header: "Registration",
            render: (r) => r.registration_no || "—",
          },
          { key: "owner_name", header: "Owner", render: (r) => r.owner_name || "—" },
          {
            key: "asset_type",
            header: "Asset",
            render: (r) => r.asset_type || "—",
          },
          {
            key: "bank_name",
            header: "Bank",
            render: (r) => r.bank_name || "—",
          },
          {
            key: "total_loan_amount",
            header: "Loan amount",
            render: (r) => fmtMoney(r.total_loan_amount),
          },
          {
            key: "loan_end_date",
            header: "Loan ends",
            render: (r) => fmtDate(r.loan_end_date),
          },
          {
            key: "first_emi_date",
            header: "First EMI",
            render: (r) => fmtDate(r.first_emi_date),
          },
          {
            key: "emi_auto_sync_enabled",
            header: "EMI auto sync",
            render: (r) => (r.emi_auto_sync_enabled === true ? "Yes" : "No"),
          },
          {
            key: "actions",
            header: "",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (r) => {
              const id = r?.id ?? r?.pk;
              if (!id) return null;
              return (
                <Link href={`/transport/vehicle-finance/${id}/edit`}>
                  <Button type="button" size="sm" variant="outline">
                    Edit
                  </Button>
                </Link>
              );
            },
          },
        ]}
        data={rows}
        isLoading={isFetching}
      />

      {!paginationOff && (
        <div className="flex justify-end">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(p) => setPage(Math.max(1, Math.min(totalPages, p)))}
          />
        </div>
      )}
    </div>
  );
}
